// @flow
export const dummyBuilder = (r: any): any => r
export const dummyMoreComing = (): boolean => false

// Types
type Poller = {|
  initPoll: Function,
  pollNext: Function,
  builder: Function,
  isMoreComing: Function,
  isResultEmpty: Function,
  isError?: Function,
  pollAll: boolean,
  maxPolls?: number,
|}

/**
 * Poller function creator for search results and all related methods
 * that require results polling
 * @param  {Function} initPoll      Function that should call endpoint to initiate a poll.
 *                                  Should retunr a promise that will be resolved with
 *                                  a response that contain `id` and `poll_id` fields.
 * @param  {Function} pollNext      Function that should call endpoint to poll next results.
 *                                  It should accept two arguments - searchid and poll id.
 *                                  Should return a Promise that should be resolved with
 *                                  some search results.
 * @param  {Function} builder       Function called with each non-empty poll result.
 * @param  {bool} pollAll           If true then poller will resolve promise
 *                                  with a list of all non-empty poll results.
 * @return {Function}
 * @private
 */
export const poller = ({
  initPoll,
  pollNext,
  builder = dummyBuilder,
  isMoreComing = dummyMoreComing,
  isResultEmpty = dummyMoreComing,
  isError = dummyMoreComing,
  pollAll = false,
  maxPolls = 60,
}: Poller): Function => {
  let id = null
  let pollId = 0
  let stopped = false
  let promise = null
  let busy = false
  let currentPolls = 1
  let moreComing = true
  let fullResults = null
  let hasError = null

  const transformResponse = (response) => {
    if (response === null) {
      return Promise.resolve(fullResults)
    }

    pollId = response.poll_id || 0
    moreComing = isMoreComing(response)
    hasError = isError(response)
    const resEmpty = isResultEmpty(response)

    if (hasError) {
      return Promise.reject(hasError)
    }
    if (resEmpty && moreComing) {
      // eslint-disable-next-line no-use-before-define
      return next()
    }
    if (resEmpty && !moreComing) {
      return Promise.resolve(fullResults)
    }

    if (pollAll) {
      return Promise.resolve(builder(response)).then((builtRes) => {
        fullResults = fullResults || []

        if (Array.isArray(builtRes)) {
          fullResults = fullResults.concat(builtRes)
        } else {
          fullResults.push(builtRes)
        }

        // eslint-disable-next-line no-use-before-define
        return next()
      })
    }
    return builder(response)
  }

  const next = (throttle = 2000) => {
    if (busy) {
      if (promise) {
        return promise.then(next)
      }
    }

    if (maxPolls < currentPolls || !moreComing || stopped) {
      return Promise.resolve(fullResults)
    }

    currentPolls += 1
    busy = true
    promise = new Promise((resolve) => {
      setTimeout(() => {
        busy = false
        resolve(throttle)
      }, throttle)
    })

    if (id === null) {
      return initPoll()
        .then((response) => {
          id = response.id || response.search_id
          pollId = response.poll_id || 0
          return response
        })
        .then(transformResponse)
    }

    return pollNext(id, pollId).then(transformResponse)
  }

  next.cancel = () => {
    stopped = true
  }
  return next
}

export default poller
